import { DatePicker, DatePickerInput } from '@carbon/react';
import { toDateValue } from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { useEffect, useRef, useState } from 'react';

const DATE_FORMAT = 'Y-m-d';
// Carbon's default pattern is the US m/d/Y; ours is ISO.
const DATE_PATTERN = '\\d{4}-\\d{2}-\\d{2}';
const DATE_VALUE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A date inside a table cell: the same Carbon date picker and `yyyy-mm-dd` value as admin-ui's DateField, with the
 * label hidden (the column heading shows it; the label still names the input for assistive technology).
 * admin-ui's DateField has no `hideLabel`, which a table of dates needs.
 *
 * A table has two of these per row, and each calendar picker starts its own calendar instance, which made a price list of
 * 150 rows take seconds to open. So a cell starts as Carbon's `simple` date input (no calendar; a date can be typed) and
 * becomes the calendar picker when it gets focus, already focused, so the calendar opens as it would have.
 */
export function CellDate({
  id,
  labelText,
  value,
  onChange,
  invalid,
  invalidText,
}: {
  id: string;
  labelText: string;
  value: string;
  onChange(value: string): void;
  invalid?: boolean;
  invalidText?: string;
}) {
  const { t } = useTranslation();
  const [active, setActive] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // The calendar picker replaces the simple input on focus: hand the focus over to it.
  useEffect(() => {
    if (active) ref.current?.querySelector('input')?.focus();
  }, [active]);

  const input = {
    id,
    labelText,
    hideLabel: true,
    placeholder: t('admin.datePlaceholder'),
    pattern: DATE_PATTERN,
    invalid,
    invalidText,
  };

  if (!active) {
    return (
      <div ref={ref}>
        <DatePicker datePickerType="simple">
          <DatePickerInput
            {...input}
            // Carbon's typings leave out `value`; the input receives it like any other attribute.
            {...({ value } as object)}
            onFocus={() => setActive(true)}
            // Focus swaps in the calendar picker before typing starts; a pasted or filled-in date still counts.
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const next = e.target.value;
              if (next === '' || DATE_VALUE.test(next)) onChange(next);
            }}
          />
        </DatePicker>
      </div>
    );
  }

  return (
    <div ref={ref}>
      <DatePicker
        datePickerType="single"
        dateFormat={DATE_FORMAT}
        value={value}
        onChange={(dates: Date[]) => onChange(dates[0] ? toDateValue(dates[0]) : '')}
      >
        <DatePickerInput {...input} />
      </DatePicker>
    </div>
  );
}
